import React, { useRef, LegacyRef } from "react";
import { View, StyleSheet, Pressable, Platform } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import { SAVE_BEHAVIOR_OPTIONS, SaveBehavior } from "@/lib/saveSettings";
import useClickOutside from "@/hooks/useClickOutside";
import type { SaveBehaviorDropdownProps } from "./types";

export function SaveBehaviorDropdown({
  entity,
  currentBehavior,
  onBehaviorChange,
  isOpen,
  onOpenChange,
}: SaveBehaviorDropdownProps) {
  const { colors } = useTheme();
  const triggerRef = useRef<View>(null);

  // Click outside to close - ignoreRef is the trigger button so clicking it doesn't immediately close
  const clickOutsideRef = useClickOutside(() => {
    if (isOpen) {
      onOpenChange(false);
    }
  }, triggerRef);

  const currentLabel = SAVE_BEHAVIOR_OPTIONS.find((opt) => opt.value === currentBehavior)?.label || "Save & Close";

  const handleSelect = (behavior: SaveBehavior) => {
    onBehaviorChange(entity, behavior);
    onOpenChange(false);
  };

  return (
    <View style={styles.container}>
      <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Save Behavior</ThemedText>
      <View style={[styles.dropdownWrapper, isOpen && styles.dropdownWrapperOpen]}>
        <Pressable
          ref={triggerRef}
          style={[styles.dropdown, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}
          onPress={() => onOpenChange(!isOpen)}
        >
          <ThemedText style={styles.dropdownText}>{currentLabel}</ThemedText>
          <MaterialCommunityIcons
            name={isOpen ? "chevron-up" : "chevron-down"}
            size={14}
            color={colors.textSecondary}
          />
        </Pressable>
        {isOpen && (
          <View
            ref={Platform.select({
              web: clickOutsideRef as unknown as LegacyRef<View>,
              default: undefined,
            })}
            style={[styles.menu, { backgroundColor: colors.background, borderColor: colors.border }]}
          >
            {SAVE_BEHAVIOR_OPTIONS.map((opt) => (
              <Pressable
                key={opt.value}
                style={[
                  styles.menuItem,
                  currentBehavior === opt.value && { backgroundColor: colors.backgroundSecondary },
                ]}
                onPress={() => handleSelect(opt.value)}
              >
                <ThemedText style={styles.menuItemText}>{opt.label}</ThemedText>
                {currentBehavior === opt.value && (
                  <MaterialCommunityIcons name="check" size={14} color={colors.primary} />
                )}
              </Pressable>
            ))}
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  label: {
    fontSize: 12,
  },
  dropdownWrapper: {
    position: "relative",
  },
  dropdownWrapperOpen: {
    zIndex: 100,
    elevation: 100,
  },
  dropdown: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
  },
  dropdownText: {
    fontSize: 12,
  },
  menu: {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: 4,
    borderRadius: 6,
    borderWidth: 1,
    overflow: "hidden",
    zIndex: 200,
    elevation: 200,
    minWidth: 160,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  menuItemText: {
    fontSize: 12,
  },
});
